import api from './api';

export interface OnboardingConfig {
  tenantId: string;
  industryType: 'HEALTHCARE' | 'MANUFACTURING' | 'FINANCE' | 'GENERAL';
  organizationSize: 'SMALL' | 'MEDIUM' | 'LARGE' | 'ENTERPRISE';
  geographicScope: 'LOCAL' | 'REGIONAL' | 'NATIONAL' | 'GLOBAL';
  customizations: {
    companyName: string;
    domain: string;
    primaryLocation: string;
    timeZone: string;
    language: string;
  };
}

export interface OnboardingResult {
  success: boolean;
  message: string;
  data?: {
    rolesCreated: number;
    usersCreated: number;
    hierarchiesCreated: number;
    templatesUsed: string[];
  };
  error?: string;
}

export interface IndustryTemplate {
  template_name: string;
  template_category: string;
  description: string;
}

export interface IndustryTemplates {
  roles: IndustryTemplate[];
  users: IndustryTemplate[];
  hierarchies: IndustryTemplate[];
}

export interface OnboardingProgress {
  onboarding_config: any;
  completion_status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  started_at: string;
  completed_at?: string;
  error_details?: string;
}

class OnboardingService {
  /**
   * Setup complete tenant onboarding
   */
  async setupTenantOnboarding(config: OnboardingConfig): Promise<OnboardingResult> {
    try {
      const response = await api.post('/onboarding/setup', config);
      return response.data;
    } catch (error: any) {
      console.error('Onboarding setup failed:', error);
      return {
        success: false,
        message: 'Failed to setup tenant onboarding',
        error: error.response?.data?.message || error.message,
      };
    }
  }

  /**
   * Get industry-specific templates
   */
  async getIndustryTemplates(industryType: string): Promise<IndustryTemplates> {
    try {
      const response = await api.get(`/onboarding/templates/${industryType}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch industry templates:', error);
      return {
        roles: [],
        users: [],
        hierarchies: [],
      };
    }
  }

  /**
   * Customize tenant setup
   */
  async customizeTenantSetup(tenantId: string, customizations: any): Promise<OnboardingResult> {
    try {
      const response = await api.post(`/onboarding/customize/${tenantId}`, customizations);
      return response.data;
    } catch (error: any) {
      console.error('Tenant customization failed:', error);
      return {
        success: false,
        message: 'Failed to customize tenant setup',
        error: error.response?.data?.message || error.message,
      };
    }
  }

  /**
   * Get onboarding progress
   */
  async getOnboardingProgress(tenantId: string): Promise<OnboardingProgress | null> {
    try {
      const response = await api.get(`/onboarding/progress/${tenantId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch onboarding progress:', error);
      return null;
    }
  }

  /**
   * Complete tenant onboarding
   */
  async completeTenantOnboarding(tenantId: string): Promise<OnboardingResult> {
    try {
      const response = await api.post(`/onboarding/complete/${tenantId}`);
      return response.data;
    } catch (error: any) {
      console.error('Onboarding completion failed:', error);
      return {
        success: false,
        message: 'Failed to complete onboarding',
        error: error.response?.data?.message || error.message,
      };
    }
  }

  /**
   * Get available industry types
   */
  async getIndustryTypes(): Promise<string[]> {
    try {
      const response = await api.get('/onboarding/industry-types');
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch industry types:', error);
      return ['GENERAL', 'HEALTHCARE', 'MANUFACTURING', 'FINANCE'];
    }
  }

  /**
   * Get available organization sizes
   */
  async getOrganizationSizes(): Promise<string[]> {
    try {
      const response = await api.get('/onboarding/organization-sizes');
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch organization sizes:', error);
      return ['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'];
    }
  }

  /**
   * Get available geographic scopes
   */
  async getGeographicScopes(): Promise<string[]> {
    try {
      const response = await api.get('/onboarding/geographic-scopes');
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch geographic scopes:', error);
      return ['LOCAL', 'REGIONAL', 'NATIONAL', 'GLOBAL'];
    }
  }
}

export const onboardingService = new OnboardingService();
